import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { addToCart } from '../../services/api';
import { useCart } from '../../context/CartContext';
import PhotoCarousel from '../common/PhotoCarousel';
import { buildAssetUrl } from '../../utils/imageResolver';
import { memberDisplayLabel } from '../MyStore/CreateLotModal';
import { isSexedPair } from '../../constants/enums';
import { weightLine } from '../../utils/weight';
import { groupLotLabel } from '../../utils/kindLabel';

/**
 * LotListingCard — I-02d Sellable Lots (slice 5, storefront exposure).
 *
 * One purchasable LOT (sexed pair / group sold as a single unit): the
 * lot gallery (the lot's own photos first, then member-attributed
 * slides — operator ruling 2026-10-07), the member roster
 * with sex glyphs, the lot title, ONE lot price, and Add to Cart pinned
 * to THIS lot (backend expects lot_id — mirrors the pinned-animal
 * pattern from AnimalListingCard).
 *
 * #98 (2026-08): `featured` renders the FLAGSHIP variant — a full-width
 * media-left/details-right presentation for the one pair lot the PDP
 * promotes above its listings grid (competitive benchmark: premium
 * pairs need premium chrome, not $60-stock-card chrome). Same data,
 * same gates, same cart wiring — layout only.
 */
/**
 * ⚠️ NO VARIATION SUBTITLE (operator, 2026-10-05). The card used to print
 * the PARENT variation's label under the lot title — the row the lot was
 * created from — so a male + female pair read "Adult Female". A lot is not
 * that variation; the roster line (♂ #… · ♀ #…) says what it is. The
 * `variationLabel` prop is still accepted (callers pass it) and ignored.
 */
const GENDER_SYMBOL = { 0: '♂', 1: '♀', 2: '?' };

function lotBadgeText(lot) {
  const members = lot.members || [];
  if (lot.lot_type === 'pair') {
    // Q-sex-certainty: "Sexed" only over two CONFIRMED sexes.
    const isSexed = isSexedPair(members);
    // One wording with the featured card (operator, 2026-10-05: the two
    // cards said "1.1 Sexed Pair" and "Sexed Pair" for the same kind of lot).
    return isSexed ? '1.1 Sexed Pair' : 'Pair';
  }
  // I-02d.2 — pool bundles have no members; N comes from lot.quantity
  if (lot.lot_type === 'pool_group') {
    return `Group of ${lot.quantity}`;
  }
  // A group of 1.2 / 2.1 is a TRIO (Q-trios-and-pairing-photos,
  // 2026-10-14) — the declared port of the server's one helper.
  return groupLotLabel(members);
}

/* #98 — flagship framing: standard herp sex-ratio notation
   (males.females) ahead of the pair label, computed from the ACTUAL
   member genders — "1.1 Sexed Pair" only when the lot really is one
   male + one female. Deliberately NOT "Proven Pair": proven is a
   breeding-history claim the lot payload carries no data to back. */
function featuredBadgeText(lot) {
  // Q-sex-certainty: "Sexed" only over two CONFIRMED sexes.
  return isSexedPair(lot.members) ? '1.1 Sexed Pair' : 'Pair';
}

function LotListingCard({ lot, variation, variationLabel, product, onAdded, featured }) {
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState(null);
  const { cartItems, refreshCart } = useCart();

  // Same reload-survival rule as AnimalListingCard: derive "In Cart"
  // from the cart itself, not just this-session click state.
  const inCart = added || (Array.isArray(cartItems) && cartItems.some(
    (i) => (i?.lot_id ?? i?.lot?.id) === lot.id
  ));

  const isPool = lot.lot_type === 'pool_group';
  const members = lot.members || [];

  // Lot gallery payload: member-sourced slides carry `member_name` +
  // `member_id` — surfaced as the per-slide caption label (member
  // attribution). Cowork F1: unnamed members caption via the shared
  // display rule; I-02d.2 tweak: unnamed captions add approx age
  // (#08DC45 · ~3.4 yr) via the roster member's out_of_water_date.
  const images = (lot.gallery || []).map((g) => {
    const rosterMember = g?.member_id
      ? members.find((m) => m.id === g.member_id)
      : null;
    return {
      url: buildAssetUrl(featured
        // adv-review SF-2: the showcase panel is ~710px wide — the ~400px
        // grid thumb upscales visibly soft on exactly the card meant to
        // look expensive. Featured prefers the ~1200px display rendition.
        ? (g?.display_url || g?.thumb_url || g?.url)
        : (g?.thumb_url || g?.url)),
      thumbUrl: buildAssetUrl(g?.thumb_url || g?.url),
      credit: (g?.credit || '').trim() || null,
      creditInfo: g?.credit_info || null,  // #110e — license pill fallback
      label: (g?.member_name || g?.member_id)
        ? memberDisplayLabel(rosterMember
          || { id: g?.member_id, name: g?.member_name })
        : null,
      // Kept for the "pictured" note below (PhotoCarousel ignores it).
      memberId: g?.member_id || null,
    };
  }).filter((g) => g.url);

  /* Photo-is-the-animal marker (competitive benchmark 2026-08): the
     unique members whose OWN photos appear in this lot's gallery.
     Pool bundles are excluded (their imagery is the species cascade —
     they wear the "Representative photo" pill instead). Short id = the
     same #FIRST6-uppercase used in the member roster. */
  const renderedSlides = images.slice(0, 10);   /* PhotoCarousel caps at 10 slides — never claim a member whose slide can't render */
  const memberSlideIds = renderedSlides.map((g) => g.memberId).filter(Boolean);
  /* A LOT-OWN slide (no member_id) is a photo the operator added to THIS
     pair/group in Manage Products — a photo OF these exact animals — so
     when one renders, every member is pictured. Since the operator ruling
     of 2026-10-07 the gallery leads with the lot's own photos (its cover
     first) and the members' own slides follow, so a MIXED gallery is the
     ordinary case: the lot-own slide still pictures everyone, and the
     per-slide caption shows only on the member slides. (Cowork audit
     2026-08-14 found the all-lot-own case printing nothing.) */
  const hasLotOwnSlide = renderedSlides.some((g) => !g.memberId);
  const picturedIds = isPool ? [] : (
    hasLotOwnSlide
      ? members.map((m) => m.id).filter(Boolean)
      : [...new Set(memberSlideIds)]
  );

  const price = lot.price != null ? Number(lot.price) : null;

  // Listing bar mirror: the API only lists purchasable lots with a
  // resolvable photo + price. A card without either is stale data —
  // render nothing (same rule as AnimalListingCard). I-02d.2: pool
  // bundles have NO photo bar (imagery is the variation/species
  // cascade, best-effort) — only the price gate applies.
  if (price == null || Number.isNaN(price)) return null;
  if (!isPool && images.length === 0) return null;

  const handleAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      await addToCart(variation.id, 1, null, lot.id);
      setAdded(true);
      // Sync CartContext so the navbar badge / other cards update
      // without a page navigation.
      if (refreshCart) refreshCart();
      if (onAdded) onAdded(lot);
    } catch (err) {
      // Message can be a serializer-errors dict — never render an
      // object into React (ADV-REVIEW #14 carryover).
      const raw = err?.response?.data?.message;
      setError(typeof raw === 'string' ? raw : 'Could not add to cart');
    } finally {
      setBusy(false);
    }
  };

  /* Shared fragments — identical data in both variants; only the
     wrapping element + type scale differ. */
  const rosterSpans = members.map((m, idx) => (
    <span key={m.id || idx} className="animal-listing-sex">
      <span className="animal-listing-sex-icon" aria-hidden="true">
        {GENDER_SYMBOL[m.gender] ?? '?'}
      </span>
      {memberDisplayLabel(m)}
      {idx < members.length - 1 ? ' · ' : ''}
    </span>
  ));

  /* Each member's weight (operator request 2026-10-07), one line per
     WEIGHED member — "♂ #08DC45 · 38 g · weighed Oct 7, 2026". A member
     with no weight is simply not listed here, and a lot with none weighed
     prints nothing at all: never a label over an empty value. */
  const weightLines = members
    .map((m, idx) => ({
      key: m.id || idx,
      glyph: GENDER_SYMBOL[m.gender] ?? '?',
      who: memberDisplayLabel(m),
      line: weightLine(m.current_weight_g, m.weighed_at),
    }))
    .filter((w) => w.line);
  const weightBlock = weightLines.length > 0 ? weightLines.map((w) => (
    <span key={w.key} className="animal-listing-weight-member" data-listing-weight>
      <span className="animal-listing-sex-icon" aria-hidden="true">{w.glyph}</span>
      {`${w.who} · ${w.line}`}
    </span>
  )) : null;

  const picturedNote = picturedIds.length > 0 ? (
    <>
      {picturedIds.length === 1 ? 'Animal ' : 'Animals '}
      {picturedIds
        .map((id) => `#${String(id).slice(0, 6).toUpperCase()}`)
        .join(', ')}
      {' pictured.'}
    </>
  ) : null;

  /* Audit item 10a (2026-08-14): shared CTA band across all listing
     cards — see .listing-cta-slot in products.css. The featured variant
     reuses it verbatim (margin-top:auto pins the CTA to the body bottom
     in that flex column too). */
  const ctaBlock = (
    <div className="listing-cta-slot">
      {error && <div className="text-danger mb-2" style={{ fontSize: '0.85rem' }}>{error}</div>}
      <button
        type="button"
        className="ff-btn ff-btn--primary ff-btn--block btn-add-to-cart"
        disabled={busy || inCart}
        onClick={handleAdd}
      >
        {inCart ? 'In Cart ✓' : busy ? 'Adding…' : 'Add to Cart'}
      </button>
    </div>
  );

  /* #98 flagship variant — full-width, media left (~55%) / details
     right; mobile stacks image over details (see .lot-featured-* in
     products.css). Pair lots only (the PDP picks which lot gets this);
     the type badge moves INTO the details column, upgraded, instead of
     overlaying the photo. Carousel auto-advances like the PDP hero —
     the grid's hover-to-advance rule exists for comparison safety
     across many idle cards, which doesn't apply to a lone showcase.
     Zero promo elements by design (zero-promo posture for flagships). */
  if (featured) {
    return (
      <section className="lot-featured-card" aria-label={`Featured listing: ${lot.title}`}>
        <div className="lot-featured-media">
          <PhotoCarousel
            images={images}
            alt={lot.title}
            imgClassName="lot-featured-image"
          />
        </div>
        <div className="lot-featured-body">
          <span className="lot-featured-badge">{featuredBadgeText(lot)}</span>
          <h3 className="lot-featured-title">{lot.title}</h3>
          <p className="lot-featured-roster mb-1">{rosterSpans}</p>
          {weightBlock && (
            <p className="mb-1 animal-listing-weight">{weightBlock}</p>
          )}
          {/* R-SHIP-AFTER (2026-10-28): the lot ships when every member can. */}
          {lot.ships_from_note && (
            <p className="mb-1 animal-listing-ships-from" data-listing-ships-from>
              {lot.ships_from_note}
            </p>
          )}
          {picturedNote && (
            <p className="listing-pictured-note mb-1">{picturedNote}</p>
          )}
          <p className="lot-featured-price">${price.toFixed(2)}</p>
          {ctaBlock}
        </div>
      </section>
    );
  }

  return (
    <div className="animal-listing-card variation-card card">
      {/* Audit item 10c (2026-08-14): pills pin to the image box
          (.listing-media-box is position:relative), not the card, so
          carousel reflow can never collide or displace them. */}
      <div className="listing-media-box">
        {/* Lots are unique listings too — same premium pill, lot wording */}
        <span className="animal-listing-ooak-badge">{lotBadgeText(lot)}</span>
        {/* Wrong-species guard (operator-found): a pool bundle's gallery is
            BY CONSTRUCTION the variation/species imagery (the N animals are
            auto-picked from stock at checkout — there are no member photos
            to show), so its photos are always representative. Pinned
            pair/group galleries are member- or lot-own photos — the actual
            animals — and get no marker. */}
        {isPool && images.length > 0 && (
          <span className="listing-rep-photo-pill">Representative photo</span>
        )}
        {images.length > 0 && (
          <PhotoCarousel
            images={images}
            alt={lot.title}
            hoverAdvance
            imgClassName="variation-image card-img-top"
          />
        )}
      </div>
      <div className="card-body variation-card-body">
        <h5 className="card-title mb-1"><strong>{lot.title}</strong></h5>
        {/* Member roster — every animal in the lot, with sex glyph.
            Cowork F1: identity (name or #SHORTID · ~age), never a bare
            role word — a buyer must know WHICH animals they're getting.
            I-02d.2 pool bundles have no roster: the N animals are
            auto-selected from stock at checkout. */}
        {isPool ? (
          <p className="card-text mb-1 animal-listing-meta text-muted">
            Pulls from available stock
          </p>
        ) : (
          <p className="card-text mb-1 animal-listing-meta">
            {rosterSpans}
          </p>
        )}
        {!isPool && weightBlock && (
          <p className="card-text mb-1 animal-listing-weight">{weightBlock}</p>
        )}
        {/* R-SHIP-AFTER (2026-10-28): the lot ships when every member can. */}
        {lot.ships_from_note && (
          <p className="card-text mb-1 animal-listing-ships-from" data-listing-ships-from>
            {lot.ships_from_note}
          </p>
        )}
        {picturedNote && (
          <p className="card-text mb-1 listing-pictured-note">
            {picturedNote}
          </p>
        )}
        <p className="card-text mb-2 animal-listing-price">
          ${price.toFixed(2)}
        </p>
        {ctaBlock}
      </div>
    </div>
  );
}

LotListingCard.propTypes = {
  lot: PropTypes.object.isRequired,
  variation: PropTypes.object.isRequired,
  variationLabel: PropTypes.string,
  product: PropTypes.object,
  onAdded: PropTypes.func,
  featured: PropTypes.bool,
};

export default LotListingCard;
